import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  ButtonBase,
  InputBase,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import {
  focusRing,
  reducedMotion,
} from "@dashboard/components/themes/themeMotion";

const SAVED_TICK_MS = 1600;

/** A settings group: grey header, white rounded rows, grey footer. */
export const SettingsGroup = ({
  header,
  footer,
  children,
}: {
  header: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const { palette } = useTheme();
  const oscar = palette.oscar.light;
  return (
    <Stack gap={1}>
      <Typography
        variant="bodyS"
        sx={{ px: 2.5, fontSize: 13, color: oscar.content.content2 }}
      >
        {header}
      </Typography>
      <Box
        sx={{
          background: oscar.surface.clear,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          // A shadow, not a border: buttons reset their borders.
          "& > * + *": { boxShadow: `inset 0 1px 0 ${oscar.borders.gray}` },
        }}
      >
        {children}
      </Box>
      {footer && (
        <Typography
          variant="bodyS"
          sx={{
            px: 2.5,
            fontSize: 13,
            lineHeight: "18px",
            color: oscar.content.content2,
          }}
        >
          {footer}
        </Typography>
      )}
    </Stack>
  );
};

export const rowSx = {
  minHeight: 52,
  px: 2.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  width: "100%",
  boxSizing: "border-box",
} as const;

const Chevron = ({ kind }: { kind: "push" | "popup" }) => {
  const stroke = useTheme().palette.oscar.light.content.content3;

  return kind === "push" ? (
    <svg width="8" height="14" viewBox="0 0 8 14" aria-hidden="true">
      <path
        d="M1 1l6 6-6 6"
        fill="none"
        stroke={stroke}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <svg width="10" height="14" viewBox="0 0 10 14" aria-hidden="true">
      <path
        d="M2 5l3-3 3 3M2 9l3 3 3-3"
        fill="none"
        stroke={stroke}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/** A row that opens something: a menu, a checklist or another page. */
export const ValueRow = React.forwardRef<
  HTMLButtonElement,
  {
    label: React.ReactNode;
    value: string;
    kind: "push" | "popup";
    open?: boolean;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  }
>(function ValueRow({ label, value, kind, open, onClick }, ref) {
  const { palette } = useTheme();
  const oscar = palette.oscar.light;
  return (
    <ButtonBase
      ref={ref}
      onClick={onClick}
      aria-haspopup={kind === "popup" ? "true" : undefined}
      aria-expanded={kind === "popup" ? Boolean(open) : undefined}
      sx={[
        rowSx,
        {
          textAlign: "left",
          background: open ? alpha(palette.common.black, 0.04) : "transparent",
          transition: "background 150ms ease-out",
          "&:hover": { background: oscar.interactive.hover1 },
          "&:first-of-type": { borderRadius: "12px 12px 0 0" },
          "&:last-of-type": { borderRadius: "0 0 12px 12px" },
          "&:only-of-type": { borderRadius: "12px" },
        },
        focusRing(-2),
        reducedMotion,
      ]}
    >
      <Typography
        component="span"
        variant="bodyRRegular"
        sx={{ fontSize: 15, display: "flex", alignItems: "center", gap: 1.25 }}
      >
        {label}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ minWidth: 0, color: oscar.content.content2 }}
      >
        <Typography
          component="span"
          variant="bodyRRegular"
          noWrap
          sx={{ fontSize: 15, color: "inherit" }}
        >
          {value}
        </Typography>
        <Chevron kind={kind} />
      </Stack>
    </ButtonBase>
  );
});

type InlineTextRowProps = {
  id: string;
  label: string;
  value: string;
  placeholder?: string;
  // An empty value reverts instead of saving.
  required?: boolean;
  onSave: (value: string) => void | Promise<unknown>;
  // Turns pasted text into the value to keep, e.g. a URL into its domain.
  cleanPaste?: (text: string) => string | null;
};

/**
 * A value edited in its own row: no box, no Save button. It saves when the
 * field loses focus and shows a tick for a moment.
 */
export const InlineTextRow = ({
  id,
  label,
  value,
  placeholder,
  required = false,
  onSave,
  cleanPaste,
}: InlineTextRowProps) => {
  const { palette } = useTheme();
  const oscar = palette.oscar.light;
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const revertRef = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), SAVED_TICK_MS);
    return () => clearTimeout(timer);
  }, [saved]);

  const commit = async () => {
    setEditing(false);
    if (revertRef.current) {
      revertRef.current = false;
      setDraft(value);
      return;
    }
    const next = draft.trim();
    if (next === value || (required && !next)) {
      setDraft(value);
      return;
    }
    await onSave(next);
    setSaved(true);
  };

  return (
    <Box
      component="label"
      htmlFor={id}
      sx={{ ...rowSx, cursor: "text", position: "relative" }}
    >
      <Typography component="span" variant="bodyRRegular" sx={{ fontSize: 15 }}>
        {label}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ flex: 1, minWidth: 0 }}
      >
        <InputBase
          id={id}
          inputRef={inputRef}
          value={draft}
          placeholder={placeholder}
          onFocus={() => {
            setEditing(true);
            setSaved(false);
          }}
          onBlur={commit}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") inputRef.current?.blur();
            if (event.key === "Escape") {
              revertRef.current = true;
              inputRef.current?.blur();
            }
          }}
          onPaste={(event) => {
            const cleaned = cleanPaste?.(event.clipboardData.getData("text"));
            if (cleaned) {
              event.preventDefault();
              setDraft(cleaned);
            }
          }}
          inputProps={{ style: { textAlign: "right" } }}
          sx={[
            {
              flex: 1,
              minWidth: 0,
              fontSize: 15,
              color: editing ? oscar.content.content1 : oscar.content.content2,
              transition: "color 150ms ease-out",
            },
            reducedMotion,
          ]}
        />
        {editing && draft && (
          <ButtonBase
            aria-label={`Clear ${label.toLowerCase()}`}
            // Keeps focus in the field so clearing doesn't save.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setDraft("");
              inputRef.current?.focus();
            }}
            sx={{
              flex: "0 0 18px",
              height: 18,
              borderRadius: "50%",
              background: oscar.content.content3,
            }}
          >
            <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
              <path
                d="M1 1l6 6M7 1L1 7"
                stroke={oscar.surface.clear}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </ButtonBase>
        )}
        <Tick shown={saved} color={oscar.content.accentText} />
        <Box
          role="status"
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
          }}
        >
          {saved ? `${label} saved` : ""}
        </Box>
      </Stack>
    </Box>
  );
};

/** The tick that confirms a choice or a save, fading in when shown. */
export const Tick = ({
  shown = true,
  size = 16,
  color,
  strokeWidth = 2,
}: {
  shown?: boolean;
  size?: number;
  color?: string;
  strokeWidth?: number;
}) => {
  const { palette } = useTheme();

  return (
    <Box
      aria-hidden="true"
      sx={[
        {
          display: "flex",
          flexShrink: 0,
          opacity: shown ? 1 : 0,
          transition: "opacity 150ms ease-out",
        },
        reducedMotion,
      ]}
    >
      <svg width={size} height={size} viewBox="0 0 16 16">
        <path
          d="M3 8.5l3 3 7-7"
          fill="none"
          stroke={color ?? palette.oscar.light.borders.electric}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
};
